import { SplitNumberInput } from '@components'

import { Color, Divider, Flex, Space, Spacing, Text } from '@billy/design-system'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { MAX_INVOICE_DISCOUNT_PERCENT } from '@features/invoices/constants/maxInvoiceDiscountPercent'
import { clampInvoiceDiscountValue } from '@features/invoices/utils/clampInvoiceDiscountValue'
import { getInvoiceDiscountTypeSelectItems } from '@features/invoices/utils/getInvoiceDiscountTypeSelectItems'
import { isAmountDiscountType } from '@features/invoices/utils/isAmountDiscountType'
import { isPercentageDiscountType } from '@features/invoices/utils/isPercentageDiscountType'

import { useFormContext, useWatch } from '../../../../hooks'
import { useDemoInvoiceLocale } from '../../hooks/useDemoInvoiceLocale'
import { DemoInvoiceFormSchema } from '../../types/demoInvoiceFormSchema'

interface DemoInvoiceGlobalDiscountProps {
  hasDiscountError: boolean
}

export const DemoInvoiceGlobalDiscount = ({ hasDiscountError }: DemoInvoiceGlobalDiscountProps): ReactElement => {
  const { t } = useTranslation()
  const locale = useDemoInvoiceLocale()
  const { control, FormItem, setValue } = useFormContext<DemoInvoiceFormSchema>()
  const [currency, discount, discountType] = useWatch({ control, name: ['currency', 'discount', 'discountType'] })

  const discountTypeSelectItems = useMemo(() => getInvoiceDiscountTypeSelectItems(currency), [currency])

  const isPercentageDiscount = isPercentageDiscountType(discountType)
  const isAmountDiscount = isAmountDiscountType(discountType)

  return (
    <Flex flexDirection="column" gap={Spacing.S4}>
      <Flex alignItems="center" gap={Spacing.S24}>
        <Text variant="bodySM" weight="semiBold">
          {t('external_invoices.editor.summary.discount')}
        </Text>
        <FormItem
          name="discount"
          render={({ field: { onChange, ...rest } }) => (
            <SplitNumberInput
              key={discountType}
              {...rest}
              alignment="left"
              aria-label={t('external_invoices.editor.summary.discount')}
              dropdownOptions={discountTypeSelectItems}
              error={hasDiscountError}
              locale={locale}
              max={isPercentageDiscount ? MAX_INVOICE_DISCOUNT_PERCENT : undefined}
              min={0}
              onChange={(discountValue) => {
                if (discountValue === undefined) {
                  return
                }

                onChange(clampInvoiceDiscountValue(discountValue, discountType))
              }}
              onOptionChange={(type) => {
                setValue('discountType', type)
                onChange(clampInvoiceDiscountValue(discount, type))
              }}
              selectedOption={discountType}
              size="sm"
              withFormatting={isAmountDiscount}
            />
          )}
        />
      </Flex>
      {hasDiscountError && (
        <Text colorVariant="danger" variant="bodySM">
          {t('external_invoices.editor.summary.discount.error')}
        </Text>
      )}
      <Space size="s6" />
      <Divider dividerColor={Color.Grey950} />
      <Space size="s8" />
    </Flex>
  )
}
